import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import serialize from 'form-serialize';
import buildActualPath from 'helpers/build-actual-path';
import Modal from 'components/Modal';
import { Translate, I18n } from 'react-redux-i18n';
import ButtonGroup from 'components/ButtonGroup';
import Form from 'components/Form';
import FormGroup from 'components/Form/FormGroup';
import Input, {
    TYPE_TEXT,
    TYPE_SELECT,
    TYPE_NUMBER,
    TYPE_TEXTAREA
} from 'components/Form/Input';
import Button from 'components/Form/Button';
import {
    NONUSER_CHARGE_EXPENSE_TYPE_GROUP,
    NONUSER_ERROR_TITLE_OUT_OF_RANGE,
    NONUSER_ERROR_AMOUNT_OUT_OF_RANGE
} from 'constants/nonuser-charge';
import { NONUSER_CHARGE_DETAIL } from 'constants/routes';
import { useCreateLinkpayMutation } from 'redux/hook/useLinkPay';
import { toggleErrorDialog } from 'actions';

const TITLE ='title';
const EXPENSE_TYPE = 'expense_type';
const AMOUNT = 'amount';
const COMMENTS = 'expense_comments';

const defaultPayload = {
    [TITLE]: '',
    [EXPENSE_TYPE]: -1,
    [AMOUNT]: '',
    [COMMENTS]: '',
};

const CreateLinkPay = ({
    show,
    onClose,
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [payload, setPayload] = useState(defaultPayload);
    const [inProgress, setInProgress] = useState(false);
    const [createLinkpay] = useCreateLinkpayMutation();

    const expenseTypeOptions = NONUSER_CHARGE_EXPENSE_TYPE_GROUP.map(value => ({
        name: I18n.t(`nonuser_charge.nonuser_expense_type_map.${ value }`),
        value: parseInt(value),
    }));
    expenseTypeOptions.unshift({ name: '', value: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPayload({
            ...payload,
            [name]: value,
        });
    }

    const handleSubmit = async e => {
        setInProgress(true);
        const formData = serialize(e.currentTarget, { hash: true });
        formData[AMOUNT] = Number(formData[AMOUNT]);
        const { data, error } = await createLinkpay(formData);
        if (error) {
            const { code } = error?.data || {};
            const message = (code !== undefined &&
                    (code === NONUSER_ERROR_AMOUNT_OUT_OF_RANGE ||
                    code === NONUSER_ERROR_TITLE_OUT_OF_RANGE)
                ) ?
                I18n.t(`nonuser_charge.create_error_message_${ code }`)
                : I18n.t('general_error_message');
            dispatch(toggleErrorDialog(message));
            setInProgress(false);
        }
        else {
            history.push(buildActualPath(NONUSER_CHARGE_DETAIL, { linkPayId: data.link_pay_id }));
        }
    };
    const handleClose = () => {
        onClose();
        setPayload(defaultPayload);
    };

    return (
        <>

            {
                show ? (
                    <Modal
                        className="create-modal"
                        title={ I18n.t('nonuser_charge.new') }
                        onClose={ handleClose }
                    >
                        <Form
                            onSubmit={ handleSubmit }
                            inProgress={ inProgress }
                            stopSubmitByEnter
                        >
                            <FormGroup title="">
                                <Input
                                    name={ TITLE }
                                    type={ TYPE_TEXT }
                                    caption="nonuser_charge.case_name"
                                    value={ payload[TITLE] }
                                    onChange={ handleChange }
                                    required
                                />
                                <Input
                                    name={ EXPENSE_TYPE }
                                    type={ TYPE_SELECT }
                                    caption="nonuser_charge.expense_type"
                                    value={ expenseTypeOptions }
                                    selected={ payload[EXPENSE_TYPE] }
                                    onChange={ handleChange }
                                    required
                                />
                                <Input
                                    name={ AMOUNT }
                                    step="0.1"
                                    type={ TYPE_NUMBER }
                                    caption="rental.price"
                                    value={ payload[AMOUNT] }
                                    onChange={ handleChange }
                                    required
                                />
                            </FormGroup>
                            <FormGroup
                                className="comment-wrapper"
                                title={ I18n.t('additional_fee.comments') }
                                withBadge={ <span className="subtitle">{ I18n.t('additional_fee.comments_ps')}</span>  }
                            >
                                <Input
                                    className="comments"
                                    name={ COMMENTS }
                                    type={ TYPE_TEXTAREA }
                                    rows={ 4 }
                                    value={ payload[COMMENTS] }
                                    onChange={ handleChange }
                                    withCaption={ false }
                                />
                            </FormGroup>
                            <ButtonGroup>
                                <Button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={ handleClose }
                                    outline
                                    stopTriggerByTab
                                >
                                    <Translate value="cancel" />
                                </Button>
                                <Button type="submit" color="primary" stopTriggerByTab>
                                    <Translate value="submit" />
                                </Button>
                            </ButtonGroup>
                        </Form>
                    </Modal>
                ) : null
            }
        </>
    );
};
CreateLinkPay.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func,
};
CreateLinkPay.defaultProps = {
    show: false,
    onClose: () => {},
};

export default React.memo(CreateLinkPay);
